// @ts-nocheck
// import StyledWrapper from "@insite/client-framework/Common/StyledWrapper"; // Starter Custom Code
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { BrandCategoriesStateContext } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import translate from "@insite/client-framework/Translate";
import { BrandCategoryModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps, LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import DataViewLoadingOverlay from "@store/Library/Components/Toolkit/DataViewLoadingOverlay";
import { LoadingOverlayProps } from "@store/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import { categoryListProps } from "@store/Themes/common/props/category";
import usePersistentDataView from "@toolkit/Framework/Common/Hooks/usePersistentDataView";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";

const StyledSpan = getStyledWrapper("span");
/* Starter Custom Code - End */

const enum fields {
    title = "title",
    showImages = "showImages",
    subCategoriesToShow = "subCategoriesToShow",
    gridType = "gridType",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.showImages]: boolean;
        [fields.subCategoriesToShow]: number;
        [fields.gridType]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => ({});

const mapDispatchToProps = {};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface BrandDetailsCategoriesStyles {
    title?: TypographyProps;
    container?: InjectableCss;
    categoryContainer?: GridContainerProps;
    categoryItemRow?: GridItemProps;
    categoryItemColumn?: GridItemProps;
    innerCategoryContainer?: GridContainerProps;
    categoryImageItem?: GridItemProps;
    categoryImageLink?: LinkPresentationProps;
    categoryImage?: LazyImageProps;
    categoryNameLinkItem?: GridItemProps;
    categoryNameLink?: LinkProps;
    subCategoryItem?: GridItemProps;
    subCategoryLink?: LinkProps;
    viewMoreLinkItem?: GridItemProps;
    viewMoreLink?: LinkProps;
    // Starter Custom Code
    loadingOverlay?: LoadingOverlayProps;
    noResult?: TypographyProps;
    categoryItem?: GridItemProps;
    categoryImageWrapper?: InjectableCss;
    categoryLink?: LinkProps;
    categoryTitle?: TypographyProps;
    subCategoryList?: TypographyProps;
    subCategoryListItem?: TypographyProps;
}

export const categoriesStyles: BrandDetailsCategoriesStyles = {
    title: categoryListProps.title,
    loadingOverlay: categoryListProps.loadingOverlay,
    noResult: categoryListProps.noResult,
    categoryContainer: categoryListProps.gridContainer,
    categoryItem: categoryListProps.gridItem,
    categoryLink: mergeProps<BrandDetailsCategoriesStyles["categoryLink"]>(categoryListProps.itemLink, {
        css: css`
            height: auto;
        `,
    }),
    categoryImageWrapper: categoryListProps.itemImageWrapper,
    categoryImage: categoryListProps.itemImage,
    categoryTitle: categoryListProps.itemTitle,
    subCategoryList: categoryListProps.itemList,
    subCategoryListItem: categoryListProps.itemListItem,
    subCategoryLink: categoryListProps.itemListLink,
    viewMoreLink: categoryListProps.itemListMoreLink,
};

const styles = categoriesStyles;

const BrandDetailsCategories: FC<Props> = ({ fields }) => {
    const brandCategoriesDataView = usePersistentDataView(useContext(BrandCategoriesStateContext)); // Starter Custom Code
    const { isLoading, value: brandCategories } = brandCategoriesDataView; // Starter Custom Code

    // Starter Removed - return null;

    const { title, showImages, gridType, subCategoriesToShow } = fields;
    const categoryItemStyles = gridType === "row" ? styles.categoryItemRow : styles.categoryItemColumn;

    const renderViewMoreLink = (category: BrandCategoryModel) => {
        if (subCategoriesToShow === 0 || (category.subCategories || []).length <= subCategoriesToShow) {
            return null;
        }
        return (
            <Typography {...styles.subCategoryListItem} /* Starter Custom Code */>
                <Link href={category.productListPagePath} {...styles.viewMoreLink}>
                    {translate("View All") /* Starter Custom Code */}
                </Link>
            </Typography>
        );
    };

    return (
        <StyledWrapper {...styles.container} data-test-selector="brandCategories">
            <Typography {...styles.title} data-test-selector="brandCategoriesTitle">
                {title}
            </Typography>
            {/* Starter Custom Code - Start */}
            <DataViewLoadingOverlay dataView={brandCategoriesDataView} {...styles.loadingOverlay}>
                {brandCategories?.length ? (
                    <GridContainer {...styles.categoryContainer}>
                        {brandCategories!.map(category => {
                            const subCategories = category.subCategories?.slice(0, subCategoriesToShow) || [];

                            return (
                                <GridItem key={category.categoryId} {...styles.categoryItem}>
                                    <Link
                                        href={category.productListPagePath}
                                        {...styles.categoryLink}
                                        data-test-selector={`brandCategoriesCategoryLink_${category.categoryId}`}
                                    >
                                        {showImages && (
                                            <StyledSpan {...styles.categoryImageWrapper}>
                                                <LazyImage
                                                    src={category.featuredImagePath}
                                                    altText={
                                                        category.featuredImageAltText ||
                                                        category.categoryShortDescription
                                                    }
                                                    {...styles.categoryImage}
                                                />
                                            </StyledSpan>
                                        )}
                                        <Typography {...styles.categoryTitle}>
                                            {category.categoryShortDescription}
                                        </Typography>
                                    </Link>
                                    {subCategories.length ? (
                                        <Typography {...styles.subCategoryList}>
                                            {subCategories.map(subCategory => (
                                                <Typography
                                                    key={subCategory.categoryId}
                                                    {...styles.subCategoryListItem}
                                                >
                                                    <Link
                                                        href={subCategory.productListPagePath}
                                                        {...styles.subCategoryLink}
                                                        data-test-selector={`brandCategoriesSubCategoryLink_${subCategory.categoryId}`}
                                                    >
                                                        {subCategory.categoryName}
                                                    </Link>
                                                </Typography>
                                            ))}
                                            {renderViewMoreLink(category)}
                                        </Typography>
                                    ) : null}
                                </GridItem>
                            );
                        })}
                    </GridContainer>
                ) : (
                    <Typography {...styles.noResult}>
                        {translate("There is no category associated to this brand.")}
                    </Typography>
                )}
            </DataViewLoadingOverlay>
            {/* Starter Custom Code - End */}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(BrandDetailsCategories),
    definition: {
        group: "Brand Details",
        displayName: "Categories",
        allowedContexts: ["BrandDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Shop By Category",
                fieldType: "Translatable",
            },
            {
                name: fields.showImages,
                displayName: "Show Images",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.subCategoriesToShow,
                displayName: "Maximum subcategories to show", // Starter Custom Code
                editorTemplate: "IntegerField",
                defaultValue: 4,
                fieldType: "General",
            },
            // Starter Removed - fields.gridType
        ],
    },
};

export default widgetModule;
